/* eslint-disable react/button-has-type */
import React, { Children } from 'react';
import PropTypes from 'prop-types';
import { classNamesFor } from '../utils/classNames';
import Media from '../Media';
import MediaFrame from '../MediaFrame';
import Icon from '../Icon';
import MultiImage from '../MultiImage';
import Alert from '../Alert';
var cx;
/**
 * The AvatarIcon component provides a unified presentation for player avatars in our UI.
 * It can represent the users profile picture, avatar or represent a group of users.
 *
 * [Mobile Design System](https://confluence.sie.sony.com/pages/viewpage.action?spaceKey=PPRC1&title=Avatars)
 *
 * ## Composed of
 * - [MultiImage](../MultiImage/README.md)
 *
 * ## Related Components
 * - [AvatarIconGroup](../AvatarIconGroup/README.md)
 * - [Image](../Image/README.md)
 * - [Media](../Media/README.md)
 *
 * ## Usage
 *
 * Single User
 *
 * ```jsx
 * <AvatarIcon
 *   size="small"
 *   src="http://profilePicOrAvatar/image.webp"
 *   alt="So and so's image"
 *   withAlert
 * />
 * ```
 *
 * Multiple Users
 *
 * ```jsx
 * <AvatarIcon size="small" withAlert>
 *    <AvatarIcon
 *       src="http://profilePicOrAvatar/image1.webp"
 *       alt="So and so's image"
 *    />
 *    <AvatarIcon
 *       src="http://profilePicOrAvatar/image2.webp"
 *       alt="So and so's image"
 *    />
 *    <AvatarIcon
 *       src="http://profilePicOrAvatar/image3.webp"
 *       alt="So and so's image"
 *    />
 *    <AvatarIcon
 *       src="http://profilePicOrAvatar/image4.webp"
 *       alt="So and so's image"
 *    />
 * </AvatarIcon>
 *```
 *
 * ## Style Hooks
 *
 * This component has the following style hooks:
 * - **root** the component root element
 * - **image** the image
 * - **alert** the alert
 *
 * ## Props
 */

var AvatarIcon = function AvatarIcon(_ref) {
  var qa = _ref.qa,
      className = _ref.className,
      src = _ref.src,
      alt = _ref.alt,
      withAlert = _ref.withAlert,
      size = _ref.size,
      children = _ref.children;

  var _AvatarIcon$getClassN = AvatarIcon.getClassName({
    className: className,
    size: size
  }),
      root = _AvatarIcon$getClassN.root,
      image = _AvatarIcon$getClassN.image;

  var widths = {
    xs: '28px',
    small: '36px',
    medium: '50px',
    large: '80px'
  };
  var childrenArray = Children.toArray(children);
  var fallbackIconSize = childrenArray.length > 2 ? 's' : size.charAt(0);
  var fallback = /*#__PURE__*/React.createElement(MediaFrame, {
    className: "psw-bg-3",
    aspectRatio: "1:1"
  }, /*#__PURE__*/React.createElement(Icon, {
    className: "psw-center",
    type: "ps-user",
    size: fallbackIconSize
  }));
  var players = childrenArray.map(function (x) {
    return {
      src: x.props.src,
      alt: x.props.alt,
      children: x.props.children
    };
  });
  var imageProps = players.length ? players : [{
    src: src,
    alt: alt,
    children: children
  }];
  return /*#__PURE__*/React.createElement(Alert, {
    qa: qa,
    className: root,
    alert1: withAlert
  }, /*#__PURE__*/React.createElement(MultiImage, {
    qa: "".concat(qa, "#multiImage"),
    className: image,
    players: players,
    width: widths[size]
  }, imageProps.map(function (x) {
    return /*#__PURE__*/React.createElement(Media, {
      aspectRatio: "1:1",
      key: x.alt,
      src: x.src,
      alt: x.alt
    }, x.children || fallback);
  })));
};
/**
 * Gets the classNames attached to the style hooks for this component.
 * @returns {string} the aliased classNames for AvatarIcon
 */


AvatarIcon.getClassName = function (_ref2) {
  var className = _ref2.className,
      size = _ref2.size;
  var imageSize = {
    xs: 'xs',
    small: 's',
    medium: 'm',
    large: 'l'
  };
  return {
    root: cx('psw-avatar-icon', className),
    image: cx('psw-avatar-icon-image', 'psw-round', 'psw-clip', "psw-l-avatar-icon-".concat(imageSize[size]))
  };
};
/**
 * Defines aliases for classNames used by this component
 */


AvatarIcon.useClassNames = function (classNames) {
  AvatarIcon.classNames = classNames;
};

AvatarIcon.defaultProps = {
  qa: '',
  className: '',
  src: '',
  alt: '',
  withAlert: false,
  size: 'medium',
  children: null
};
AvatarIcon.propTypes = {
  /**
   * QA selector attribute used to target important elements for test validation
   */
  qa: PropTypes.string,

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  className: PropTypes.string,

  /**
   * The image src for the users image
   */
  src: PropTypes.string,

  /**
   * Text describing what the image is
   * [WebAim on alt text](https://webaim.org/techniques/alttext/)
   */
  alt: PropTypes.string,

  /**
   * Visual indicator representing... TBC
   */
  withAlert: PropTypes.bool,

  /**
   * The size of the avatar to display
   */
  size: PropTypes.oneOf(['xs', 'small', 'medium', 'large']),

  /**
   * Used to pass multiple avatar images. See usage above.
   */
  children: PropTypes.node
};
cx = classNamesFor(AvatarIcon);
export default AvatarIcon;